import {
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  Crown,
  Flame,
  type LucideIcon,
  TrendingDown,
} from "lucide-react";

export const MAYA = {
  name: "Maya Rivera",
  src: "/images/website/customers/maya-rivera.jpg",
  since: "Jan 2024",
  location: "River North",
} as const;

export type JourneyStepId = "active" | "lapse" | "winback" | "returned" | "rhythm" | "upgraded";

export type JourneyStep = { id: JourneyStepId; label: string };

// Ordered milestones shown in the spotlight stepper. The thread drives which
// one is active as Maya lapses, re-engages, and upgrades.
export const JOURNEY: JourneyStep[] = [
  { id: "active", label: "4× / month member" },
  { id: "lapse", label: "Attendance dropped" },
  { id: "winback", label: "Win-back sent" },
  { id: "returned", label: "Back in class" },
  { id: "rhythm", label: "Back in rhythm" },
  { id: "upgraded", label: "Upgraded to Unlimited" },
];

export const JOURNEY_ORDER = JOURNEY.map((s) => s.id);

export type Offer = { brand: string; title: string; detail: string; cta: string };

// One chronological thread combining customer events, autopilot explainers and
// SMS. `delay` is how long each item holds as the latest before the next
// reveals. `step` advances the journey stepper.
export type ThreadItem =
  | {
      kind: "event";
      id: string;
      icon: LucideIcon;
      title: string;
      sub?: string;
      step?: JourneyStepId;
      delay: number;
    }
  | { kind: "system"; id: string; text: string; delay: number }
  | {
      kind: "message";
      id: string;
      direction: "incoming" | "outgoing";
      body: string;
      offer?: Offer;
      step?: JourneyStepId;
      delay: number;
    };

export const TIMELINE: ThreadItem[] = [
  {
    kind: "event",
    id: "active",
    icon: BadgeCheck,
    title: "Active member · 4× per month",
    sub: "186 classes since Jan 2024",
    step: "active",
    delay: 1200,
  },
  {
    kind: "system",
    id: "watch",
    text: "Helios watches every member's attendance",
    delay: 1100,
  },
  {
    kind: "event",
    id: "drop",
    icon: TrendingDown,
    title: "Attendance dropped",
    sub: "0 visits in 21 days",
    step: "lapse",
    delay: 1400,
  },
  {
    kind: "system",
    id: "flag",
    text: "Auto-flagged churn risk → win-back flow",
    delay: 1200,
  },
  {
    kind: "message",
    id: "wb-1",
    direction: "outgoing",
    body: "Hey Maya! We miss you at Fitt Pilates 💛 Your favorite Sculpt class has a spot tomorrow at 6:00am. Want it?",
    step: "winback",
    delay: 2000,
  },
  {
    kind: "message",
    id: "wb-2",
    direction: "incoming",
    body: "Oh I've been meaning to come back! Yes please book me 🙏",
    delay: 1600,
  },
  {
    kind: "message",
    id: "wb-3",
    direction: "outgoing",
    body: "Done! You're booked for Sat 6:00am ☀️ See you there!",
    delay: 1500,
  },
  {
    kind: "event",
    id: "booked",
    icon: CalendarCheck,
    title: "Booked Sculpt",
    sub: "Sat · 6:00 AM with Mia",
    delay: 1300,
  },
  {
    kind: "event",
    id: "checkin",
    icon: CheckCircle2,
    title: "Checked in · back in class",
    step: "returned",
    delay: 1300,
  },
  {
    kind: "system",
    id: "momentum",
    text: "Came to 3 classes over the next 2 weeks",
    delay: 1200,
  },
  {
    kind: "event",
    id: "rhythm",
    icon: Flame,
    title: "Back in rhythm",
    sub: "3 visits in 2 weeks",
    step: "rhythm",
    delay: 1300,
  },
  {
    kind: "system",
    id: "opp",
    text: "Visiting more than 4× → upgrade opportunity",
    delay: 1100,
  },
  {
    kind: "message",
    id: "upsell",
    direction: "outgoing",
    body: "You're on a roll, Maya! 🔥 At this pace Unlimited saves you money every month. Want me to switch you over?",
    offer: {
      brand: "Fitt Pilates",
      title: "Switch to Unlimited",
      detail: "Unlimited classes · $160/mo",
      cta: "Upgrade membership",
    },
    delay: 2100,
  },
  {
    kind: "message",
    id: "upsell-yes",
    direction: "incoming",
    body: "Yes please, let's do it! 🎉",
    delay: 1500,
  },
  {
    kind: "event",
    id: "upgraded",
    icon: Crown,
    title: "Upgraded to Unlimited",
    sub: "Was 4× per month",
    step: "upgraded",
    delay: 1800,
  },
];
